import { AdjustAdjustments } from './adjust.types'
import { clarityRadiusFor, toShaderUniforms } from './adjustColor'
import { logger } from 'utils/logging'

const VERTEX_SHADER = `
attribute vec2 a_position;
varying vec2 v_texCoord;
void main() {
  gl_Position = vec4(a_position, 0.0, 1.0);
  v_texCoord = vec2(a_position.x * 0.5 + 0.5, 0.5 - a_position.y * 0.5);
}
`

/**
 * The in-house editor's single-pass adjustment shader, with clarity corrected (detail = source − its local average,
 * at a radius that follows the photo's size) — mirrored by `processPixels` in adjustColor.ts.
 */
const FRAGMENT_SHADER = `
#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
uniform sampler2D u_image;
uniform vec2 u_texel;
uniform float u_radius;
uniform float u_exposure;
uniform float u_brightness;
uniform float u_contrast;
uniform float u_highlights;
uniform float u_shadows;
uniform float u_saturation;
uniform float u_temperature;
uniform float u_tint;
uniform float u_gamma;
uniform float u_clarity;
uniform float u_vignette;
varying vec2 v_texCoord;

void main() {
  vec4 texColor = texture2D(u_image, v_texCoord);
  vec3 source = texColor.rgb;
  vec3 color = source * pow(2.0, u_exposure) + u_brightness;
  color = (color - 0.5) * (1.0 + u_contrast) + 0.5;
  float lum = dot(color, vec3(0.299, 0.587, 0.114));
  color += vec3(u_highlights * smoothstep(0.5, 1.0, lum) * 0.5);
  color += vec3(u_shadows * (1.0 - smoothstep(0.0, 0.5, lum)) * 0.5);
  float gray = dot(color, vec3(0.299, 0.587, 0.114));
  color = mix(vec3(gray), color, 1.0 + u_saturation);
  color.r += u_temperature * 0.1;
  color.b -= u_temperature * 0.1;
  color.g += u_tint * 0.1;
  if (abs(u_gamma - 1.0) > 0.001) {
    color = pow(max(color, 0.0), vec3(1.0 / u_gamma));
  }
  if (u_clarity != 0.0) {
    vec2 o = u_texel * u_radius;
    vec3 average = (texture2D(u_image, v_texCoord + vec2(-o.x, -o.y)).rgb
      + texture2D(u_image, v_texCoord + vec2(o.x, -o.y)).rgb
      + texture2D(u_image, v_texCoord + vec2(-o.x, o.y)).rgb
      + texture2D(u_image, v_texCoord + vec2(o.x, o.y)).rgb) * 0.25;
    color += (source - average) * u_clarity * 1.5;
  }
  if (u_vignette != 0.0) {
    float dist = length(v_texCoord - 0.5) * 1.414;
    color *= 1.0 - smoothstep(0.5, 1.5, dist) * u_vignette;
  }
  gl_FragColor = vec4(clamp(color, 0.0, 1.0), texColor.a);
}
`

const UNIFORMS = ['u_image', 'u_texel', 'u_radius', 'u_exposure', 'u_brightness', 'u_contrast', 'u_highlights', 'u_shadows', 'u_saturation', 'u_temperature', 'u_tint', 'u_gamma', 'u_clarity', 'u_vignette'] as const

type UniformName = typeof UNIFORMS[number]

/**
 * GPU path for Fine tune and Filters. One instance per editor; `create()` returns null when WebGL is unavailable, and
 * `render()` returns null when it cannot draw (context lost, photo larger than the GPU allows) — the caller then uses
 * the CPU path. Unlike the in-house processor it checks MAX_TEXTURE_SIZE and listens for context loss.
 */
export class WebGLAdjustProcessor {
  private readonly canvas: HTMLCanvasElement
  private readonly gl: WebGLRenderingContext
  private readonly program: WebGLProgram
  private readonly texture: WebGLTexture
  private readonly uniforms: Record<UniformName, WebGLUniformLocation | null>
  private uploadedSource: TexImageSource | null = null
  private isLost = false
  readonly maxTextureSize: number

  private constructor(canvas: HTMLCanvasElement, gl: WebGLRenderingContext, program: WebGLProgram, texture: WebGLTexture) {
    this.canvas = canvas
    this.gl = gl
    this.program = program
    this.texture = texture
    this.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number
    this.uniforms = Object.fromEntries(UNIFORMS.map(name => [name, gl.getUniformLocation(program, name)])) as Record<UniformName, WebGLUniformLocation | null>
    canvas.addEventListener('webglcontextlost', this.handleContextLost)
  }

  private readonly handleContextLost = (event: Event) => {
    event.preventDefault()
    this.isLost = true
    this.uploadedSource = null
  }

  static create(): WebGLAdjustProcessor | null {
    try {
      const canvas = document.createElement('canvas')
      const gl = canvas.getContext('webgl', { preserveDrawingBuffer: true, premultipliedAlpha: false }) as WebGLRenderingContext | null
      if (!gl) return null
      const compile = (type: number, source: string) => {
        const shader = gl.createShader(type)
        if (!shader) throw new Error('webgl_shader_create_failed')
        gl.shaderSource(shader, source)
        gl.compileShader(shader)
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(shader) || 'webgl_shader_compile_failed')
        return shader
      }
      const program = gl.createProgram()
      if (!program) return null
      gl.attachShader(program, compile(gl.VERTEX_SHADER, VERTEX_SHADER))
      gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FRAGMENT_SHADER))
      gl.linkProgram(program)
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(program) || 'webgl_program_link_failed')
      gl.useProgram(program)

      const buffer = gl.createBuffer()
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW)
      const position = gl.getAttribLocation(program, 'a_position')
      gl.enableVertexAttribArray(position)
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)

      const texture = gl.createTexture()
      if (!texture) return null
      gl.bindTexture(gl.TEXTURE_2D, texture)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
      return new WebGLAdjustProcessor(canvas, gl, program, texture)
    } catch (error) {
      logger.warn('Adjust editor: WebGL unavailable, using the CPU path', error, { context: { component: 'PhotoStudio', action: 'adjustWebGLInit' } })
      return null
    }
  }

  canRender(width: number, height: number): boolean {
    return !this.isLost && !this.gl.isContextLost() && width <= this.maxTextureSize && height <= this.maxTextureSize
  }

  /** Draws `source` with `adjustments` into the processor's canvas and returns it (null = use the CPU path) */
  render(source: TexImageSource, width: number, height: number, adjustments: AdjustAdjustments): HTMLCanvasElement | null {
    if (!this.canRender(width, height)) return null
    const gl = this.gl
    try {
      if (this.canvas.width !== width || this.canvas.height !== height) {
        this.canvas.width = width
        this.canvas.height = height
      }
      gl.viewport(0, 0, width, height)
      gl.useProgram(this.program)
      gl.bindTexture(gl.TEXTURE_2D, this.texture)
      if (this.uploadedSource !== source) {
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false)
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source)
        this.uploadedSource = source
      }
      const u = toShaderUniforms(adjustments)
      gl.uniform1i(this.uniforms.u_image, 0)
      gl.uniform2f(this.uniforms.u_texel, 1 / width, 1 / height)
      gl.uniform1f(this.uniforms.u_radius, clarityRadiusFor(width, height))
      gl.uniform1f(this.uniforms.u_exposure, u.exposure)
      gl.uniform1f(this.uniforms.u_brightness, u.brightness)
      gl.uniform1f(this.uniforms.u_contrast, u.contrast)
      gl.uniform1f(this.uniforms.u_highlights, u.highlights)
      gl.uniform1f(this.uniforms.u_shadows, u.shadows)
      gl.uniform1f(this.uniforms.u_saturation, u.saturation)
      gl.uniform1f(this.uniforms.u_temperature, u.temperature)
      gl.uniform1f(this.uniforms.u_tint, u.tint)
      gl.uniform1f(this.uniforms.u_gamma, u.gamma)
      gl.uniform1f(this.uniforms.u_clarity, u.clarity)
      gl.uniform1f(this.uniforms.u_vignette, u.vignette)
      gl.drawArrays(gl.TRIANGLES, 0, 6)
      return this.canvas
    } catch (error) {
      logger.warn('Adjust editor: WebGL render failed, using the CPU path', error, { context: { component: 'PhotoStudio', action: 'adjustWebGLRender' } })
      return null
    }
  }

  /** The next render re-uploads its source (call after drawing into a reused source canvas) */
  invalidate(): void {
    this.uploadedSource = null
  }

  dispose(): void {
    this.canvas.removeEventListener('webglcontextlost', this.handleContextLost)
    this.gl.deleteTexture(this.texture)
    this.gl.deleteProgram(this.program)
    this.gl.getExtension('WEBGL_lose_context')?.loseContext()
  }
}
